import { useEffect, useState } from "react";
import { Card, Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, CancelButton } from "ui-components/IconButton";
import { Success, Error_, MessageDialog } from "ui-components/Alert";
import BasicTabs from "ui-components/BasicTabs";
import { useCustomerValue } from "providers/CustomerContext";
import {
  getRecvStatusCount,
  getRecvQueueStatusCount,
  deletePOReceiving,
  createPalletize,
} from "services/data.inbound.service";
import { statusObj } from "layouts/views/common/enum";
import PORecvLog from "./components/PORecvLog";
import BasicQueueTabs from "ui-components/BasicQueueTabs";
import { LoadingDialog } from "ui-components/Alert";
import { getReceivingList } from "services/data.inbound.service";
import { TableCard } from "ui-components/Layout";
import { useTeamValue } from "providers/TeamContext";
import RecvQtyLog from "../receiving/components/RecvQtyLog";
import MDBadge from "components/MDBadge";

function PORecvList() {
  const {permissions, allowAll} = useTeamValue();
  const { customerCode: custCode } = useCustomerValue();
  const enabled = Boolean(custCode);
  const navigate = useNavigate();
  const location = useLocation()
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Open");
  const [queueStatus, setQueueStatus] = useState("");
  const type = "PORECV";
  const [tabs, setTabs] = useState([{label:"Open",count:0},{label:"Cancelled",count:0},{label:"Received",count:0}]);
  const [queueTabs, setQueueTabs] = useState([]);
  const [click, setClick] = useState(false)
  const queryClient = useQueryClient();
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const addPermission = allowAll || (Boolean(permissions["PO Receiving"]) && permissions["PO Receiving"].findIndex(b => b==="new") !== -1);
  const editPermission = allowAll || (Boolean(permissions["PO Receiving"]) && permissions["PO Receiving"].findIndex(b => b==="edit") !== -1);
  const deactivatePermission = allowAll || (Boolean(permissions["PO Receiving"]) && permissions["PO Receiving"].findIndex(b => b==="deactivate") !== -1);

  useEffect(() => {
    if (location.state) {
      setAlert(<Success content={location.state} onChange={setAlert} />);
    }
  }, []);

  const { isRefetching } = useQuery({
    queryKey: ["po-recv-list", { custCode, status, queueStatus }],
    queryFn: () => getReceivingList({ custCode, status: statusObj[status], type, queueStatus }),
    enabled,
    onSuccess: (data) => setData(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["po-recv-list", "status", { custCode, status, queueStatus }],
    queryFn: () => getRecvStatusCount({ custCode, type }),
    enabled,
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  useQuery({
    queryKey: ["po-recv-list", "queue-status", { custCode, status, queueStatus }],
    queryFn: () => getRecvQueueStatusCount({ custCode, type }),
    enabled: Boolean(custCode) && status === "Open",
    onSuccess: (data) => setQueueTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deletePOReceiving, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["po-recv-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });

  const palletizeMutation = useMutation(createPalletize, {
    onSuccess: (data) => {
      queryClient.invalidateQueries(["po-recv-list"]);
      setAlert(<Success data={data} onChange={setAlert} />);
    },
    onError,
  });
  const palletizeStatus = palletizeMutation.status === "loading";

  const onEdit = (req) => {
    navigate("/inbound/po-receiving/edit", {
      state: req,
    });
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Cancel PO Receiving Code ${req.recvCode}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          delMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const [logRequest, setLogRequest] = useState(null);
  const [open, setOpen] = useState(false);
  const logProp = { open, setOpen, logRequest, setLogRequest };
  const [qtyLogRequest, setQtyLogRequest] = useState(null);
  const [qtyOpen, setQtyOpen] = useState(false);
  const qtyLogProp = {
    open: qtyOpen,
    setOpen: setQtyOpen,
    logRequest: qtyLogRequest,
    setLogRequest: setQtyLogRequest,
  };

  const columns = [
    {
      header: "Receiving Code",
      accessorKey: "recvCode",
      width:"150px",
      cell: ({ row }) => {
        const { recvCode, customerCode } = row.original;
        return (
          <span
            style={{ color: "#1A73E8", cursor: "pointer", textDecoration: "underline" }}
            onClick={() => {
              setOpen(true);
              setLogRequest({ recvCode, customerCode });
            }}
          >
            {recvCode}
          </span>
        );
      },
       
    },
    { header: "Receiving Date", accessorKey: "dateReceived",width:"150px" },
    { header: "PO Number", accessorKey: "poNumber",width:"150px" },
    { header: "ASN Code", accessorKey: "asnCode",width:"150px" },
    { header: "Vendor Code", accessorKey: "vendorCode",width:"150px" },
    {
      header: "Split Qty",
      accessorKey: "splitQty",
      width: "150px",
      cell: ({ row }) => {
        const { recvCode, splitQty, canPalletize } = row.original;
        if (splitQty) {
          const onClick = () => {
            setQtyOpen(true);
            setQtyLogRequest({ recvCode,canPalletize });
          };
          if (!canPalletize) {
            return <Badge color="warning" content="Scanning" onClick={onClick} />;
          } else {
            return <Badge color="success" content="Complete" onClick={onClick} />;
          }
        }
        return <div></div>;
      },
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { recvCode, customerCode, canPalletize } = row.original;

        if (status === "Open" && queueStatus !== "NEW") {
          const OpenComponent = (
            <>
              <EditButton
                title="edit po receiving"
                disabled={!editPermission}
                onClick={() => onEdit({ recvCode, customerCode })}
              />
              <CancelButton
                title="cancel po receiving"
                disabled={!deactivatePermission}
                onClick={() => onDelete({ recvCode, customerCode })}
              />
              <MDButton
                sx={{ ml: "10px" }}
                variant="gradient"
                color="info"
                size="small"
                disabled={!canPalletize || click}
                onClick={() => {
                  setClick(true) 
                  palletizeMutation.mutate({ recvCode, customerCode },{
                    onSettled:() => setClick(false) 
                  }) 
                }}
              >
                {queueStatus === "" ? "palletize" : "resend palletize"}
              </MDButton>
            </>
          );
          return OpenComponent;
        } else if (status === "Palletize") {
          const PalletizeComponent = (
            <>
              <MDButton sx={{ ml: "10px" }} variant="gradient" color="error" size="small">
                cancel palletize
              </MDButton>
            </>
          );
          return PalletizeComponent;
        } else {
          return <></>;
        }
      },
    },
  ];
  const rows = data;
  return (
    <ListLayout>
      <PORecvLog {...logProp} />
      <RecvQtyLog {...qtyLogProp} />
      {(isRefetching || palletizeStatus) && <LoadingDialog />}
       
      {dialog}
      <MDBox my={3}>
      {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
        {status === "Open" && queueTabs.length > 0 && (
          <BasicQueueTabs
            queueTabs={queueTabs}
            queueStatus={queueStatus}
            setQueueStatus={setQueueStatus}
          />
        )}
      </MDBox>

      <TableCard>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" margin="10px">
          <MDButton
            variant="gradient"
            color="info"
            disabled={!addPermission}
            onClick={() => navigate("/inbound/po-receiving/add")}
          >
            <Icon>add</Icon>&nbsp;po receiving
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={rows} />
      </TableCard>
    </ListLayout>
  );
}

const Badge = ({ color, content, onClick }) => {
  return (
    <MDBadge
      sx={{ cursor: "pointer" }}
      onClick={onClick}
      variant="gradient"
      color={color}
      size="xs"
      badgeContent={content}
      container
    />
  );
};

export default PORecvList;
